import React, { useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { Organization, User } from '@logic'
import { HttpClient, HttpError } from '@src/service/lib/http-client'
import { withService } from '@shared/hocs/with-service'
import { PageContent } from '@components/pages'
import { useMattermostSession } from '../hooks/use-mattermost-session'
import { MattermostEmbed } from '../components/mattermost-embed'
import { MessagingLoading } from '../components/messaging-loading'
import { MessagingError } from '../components/messaging-error'
import { syncOrgMattermost } from '../services/mattermost-api'
import { useBuzzMessaging } from '../hooks/use-buzz-messaging'
import { BuzzMessagingShell } from '../components/buzz-messaging-shell'
import { createBuzzLocalFixture } from '../services/buzz-local-fixture'
import { MessagingProvider, resolveMessagingProvider } from '../services/messaging-provider'
import { buildCanonicalBuzzInput } from '../services/buzz-bootstrap-input'

interface StateProps {
  currentOrganization?: Organization
  currentUser?: User
  isAdmin: boolean
}

type Props = StateProps & {
  httpClient: HttpClient
}

const MessagingPageComponent: React.FC<Props> = ({ currentOrganization, httpClient, isAdmin }) => {
  const orgId = currentOrganization?.id
  const { session, loading, error, refresh } = useMattermostSession(httpClient, orgId)
  const [syncing, setSyncing] = useState(false)
  const [syncStatus, setSyncStatus] = useState<string | null>(null)

  const handleSync = async () => {
    if (!orgId) return
    setSyncing(true)
    setSyncStatus(null)
    try {
      const result = await syncOrgMattermost(httpClient, orgId)
      if (result instanceof HttpError) {
        setSyncStatus(`Sync failed: ${result.message || `Error ${result.status}`}`)
      } else {
        setSyncStatus(`Synced ${result.synced} member(s)`)
      }
    } catch (err: any) {
      setSyncStatus(`Sync failed: ${err?.message || 'Unknown error'}`)
    } finally {
      setSyncing(false)
    }
  }

  if (loading) {
    return (
      <PageContent>
        <MessagingLoading />
      </PageContent>
    )
  }

  if (error || !session) {
    return (
      <PageContent>
        <MessagingError
          message={error || 'Messaging is not available for this organization.'}
          onRetry={refresh}
        />
      </PageContent>
    )
  }

  return (
    <div style={{ position: 'relative' }}>
      {isAdmin && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px' }}>
          <button
            onClick={handleSync}
            disabled={syncing}
            style={{
              padding: '6px 12px',
              fontSize: '13px',
              cursor: syncing ? 'not-allowed' : 'pointer',
              opacity: syncing ? 0.6 : 1,
            }}
          >
            {syncing ? 'Syncing...' : 'Sync Members'}
          </button>
          {syncStatus && <span style={{ fontSize: '13px' }}>{syncStatus}</span>}
        </div>
      )}
      <MattermostEmbed
        baseUrl={session.baseUrl}
        token={session.token}
        teamName={session.teamName}
      />
    </div>
  )
}

const BuzzMessagingPageComponent: React.FC<Props> = ({ currentOrganization, currentUser }) => {
  const fixtureInput = createBuzzLocalFixture(
    window.location.pathname,
    window.location.search,
    process.env.REACT_APP_BUZZ_FIXTURE_AUTH === 'true',
  )
  const canonicalInput = useMemo(() => buildCanonicalBuzzInput(
    currentOrganization,
    currentUser,
  ), [currentOrganization, currentUser])
  const input = fixtureInput || canonicalInput
  const messaging = useBuzzMessaging(input)

  if (messaging.loading) {
    return <PageContent><MessagingLoading /></PageContent>
  }
  if (!messaging.session) {
    return (
      <PageContent>
        <MessagingError
          message={messaging.error || 'Equa Chat is not available for this organization.'}
          onRetry={() => { void messaging.refresh() }}
        />
      </PageContent>
    )
  }
  return <BuzzMessagingShell {...messaging} />
}

const mapStateToProps = (state: any): StateProps => ({
  currentOrganization: state.organization?.currentOrganization,
  currentUser: state.user,
  isAdmin: state.organization?.currentMemberRoles?.some(
    (r: any) => r.name === 'Admin'
  ) ?? false,
})

// Lazy per-provider initialization avoids the webpack scope-hoisting TDZ while
// still allowing SPA navigation to switch between the fixture and normal route.
const messagingPages: Partial<Record<MessagingProvider, React.ComponentType<any>>> = {}
export const MessagingPage: React.FC<any> = (props: any) => {
  const fixtureRoute = Boolean(createBuzzLocalFixture(
    window.location.pathname,
    window.location.search,
    process.env.REACT_APP_BUZZ_FIXTURE_AUTH === 'true',
  ))
  const provider = resolveMessagingProvider(
    process.env.REACT_APP_MESSAGING_PROVIDER,
    fixtureRoute,
  )
  if (!messagingPages[provider]) {
    const implementation = provider === 'buzz'
      ? BuzzMessagingPageComponent
      : MessagingPageComponent
    messagingPages[provider] = connect(mapStateToProps)(withService(implementation))
  }
  return React.createElement(messagingPages[provider]!, props)
}
